import {
  getValueOfLimit,
  isActiveUsersLimitType,
  storageUnitToBytes,
} from "@utils/limit-utils";
import { Grid } from "@mui-wrapper";
import { Fragment, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  plansCreationStateSelector,
  setAllLimitByType,
  setTokensPerYear,
} from "@store/plan-creation/plan-creation-slice";
import { sceneSeatsToCreateSelector } from "@store/plan-creation/plan-creation-selectors";
import { SCENE_SEATS_DISPLAY_NAME } from "@components/plans-table/scene-licenses";
import { isEnterpriseProductIdentifier } from "@utils/type-guards";
import { PlanLimitTierSelection } from "@components/forms/plan-limit-tier-selection";
import {
  CREDITS_DISPLAY_NAME,
  formatLimitType,
  LimitStorageUnit,
} from "@utils/limit-data-display";
import { capitalize } from "es-toolkit/string";
import { Checkbox, Dropdown, TextField } from "@flat-ui";

/** Shared sizing so the storage unit field lines up with the limit text fields */
const FIELD_VERTICAL_PADDING = "16.5px";

/** The horizontal padding of the text field input box */
const FIELD_HORIZONTAL_PADDING = "14px";

/**
 * Pushes the label-less checkbox down so it vertically centers on the input box
 * (the row is top-aligned, and the fields sit below their static label + `mt`).
 */
const CHECKBOX_TOP_OFFSET = 6;

/** A selectable option in the storage unit dropdown */
interface StorageUnitOption {
  key: LimitStorageUnit;
  label: string;
  value: LimitStorageUnit;
}

/** Options for the storage unit dropdown */
const STORAGE_UNIT_OPTIONS: StorageUnitOption[] = [
  { key: "GB", label: "GB", value: "GB" },
  { key: "TB", label: "TB", value: "TB" },
];

/** Wraps all the logics and UIs related to plan limits */
export function PlanLimitForm(): React.JSX.Element {
  const { selectedProduct, allLimits, tokensPerYear } = useAppSelector(
    plansCreationStateSelector
  );
  const sceneSeatsToCreate = useAppSelector(sceneSeatsToCreateSelector);

  const dispatch = useAppDispatch();

  const hasMultipleLimits = allLimits.length > 1;

  // Auto-enable limit when there's only one
  useEffect(() => {
    if (!hasMultipleLimits) {
      dispatch(
        setAllLimitByType({
          type: allLimits[0].type,
          isEnabled: true,
        })
      );
    }
  }, [hasMultipleLimits, allLimits, dispatch]);

  const shouldShowTier = isEnterpriseProductIdentifier(
    selectedProduct?.identifier
  );

  return (
    <Grid container spacing={1}>
      {allLimits.map((limit) => {
        // Enterprise plans pick their storage through a tier instead of a free value
        if (shouldShowTier && limit.type === "STORAGE") {
          return (
            <Grid key={limit.type} size={{ xs: 12 }} sx={{ mt: 2 }}>
              <PlanLimitTierSelection selectedProduct={selectedProduct} />
            </Grid>
          );
        }

        const storageUnit: LimitStorageUnit = limit.storageUnit ?? "GB";
        const shouldShowSceneSeats =
          isActiveUsersLimitType(limit.type) && selectedProduct?.hasSceneSeats;
        // A fixed limit's value stays locked even while the limit is enabled
        const isValueLocked = !limit.isEnabled || limit.isFixed;

        return (
          <Fragment key={limit.type}>
            <Grid
              size={{ xs: 12 }}
              container
              spacing={1}
              wrap="nowrap"
              sx={{ alignItems: "flex-start" }}
            >
              {hasMultipleLimits && (
                <Checkbox
                  checked={limit.isEnabled}
                  disabled={limit.isRequired}
                  onChange={(event) => {
                    dispatch(
                      setAllLimitByType({
                        type: limit.type,
                        isEnabled: event.target.checked,
                      })
                    );
                  }}
                  controlSx={{ mt: CHECKBOX_TOP_OFFSET }}
                />
              )}
              <TextField
                type="number"
                label={formatLimitType(limit.type)}
                error={
                  limit.isEnabled && limit.value <= 0
                    ? `Please enter a valid ${formatLimitType(
                        limit.type
                      )} limit`
                    : undefined
                }
                text={String(getValueOfLimit(limit, storageUnit)) || undefined}
                onTextChanged={(newLimitValue: string) => {
                  dispatch(
                    setAllLimitByType({
                      type: limit.type,
                      value:
                        limit.type === "STORAGE"
                          ? storageUnitToBytes(
                              Number(newLimitValue),
                              storageUnit
                            )
                          : Number(newLimitValue),
                    })
                  );
                }}
                controlStyle={{
                  mt: 2,
                  "& .MuiOutlinedInput-input": {
                    p: `${FIELD_VERTICAL_PADDING} ${FIELD_HORIZONTAL_PADDING}`,
                  },
                }}
                fullWidth
                disabled={isValueLocked}
              />
              {limit.type === "STORAGE" && (
                <Dropdown
                  label="Unit"
                  value={storageUnit}
                  options={STORAGE_UNIT_OPTIONS}
                  disabled={isValueLocked}
                  onChange={(event) => {
                    const newStorageUnit = event.target.value;
                    dispatch(
                      setAllLimitByType({
                        type: limit.type,
                        storageUnit: newStorageUnit,
                        // Keep the entered number, reinterpreting it in the new unit
                        value: storageUnitToBytes(
                          getValueOfLimit(limit, storageUnit),
                          newStorageUnit
                        ),
                      })
                    );
                  }}
                  containerSx={{ mt: 2, width: "6rem" }}
                  sx={{ height: "3.5rem", boxSizing: "border-box" }}
                />
              )}
            </Grid>

            {/* One SCENE seat is included per active user, so the seats always
                follow the "Active Users" limit and cannot be edited on their own */}
            {shouldShowSceneSeats && (
              <Grid
                size={{ xs: 12 }}
                container
                spacing={1}
                wrap="nowrap"
                sx={{ alignItems: "flex-start" }}
              >
                {hasMultipleLimits && (
                  <Checkbox
                    checked
                    disabled
                    controlSx={{ mt: CHECKBOX_TOP_OFFSET }}
                  />
                )}
                <TextField
                  type="number"
                  label={SCENE_SEATS_DISPLAY_NAME}
                  text={String(sceneSeatsToCreate)}
                  controlStyle={{
                    mt: 2,
                    "& .MuiOutlinedInput-input": {
                      p: `${FIELD_VERTICAL_PADDING} ${FIELD_HORIZONTAL_PADDING}`,
                    },
                  }}
                  fullWidth
                  disabled
                />
              </Grid>
            )}
          </Fragment>
        );
      })}

      {/* The tokens are granted through separate plans that are attached to the plan, one for
          every year it runs, so they are not part of the limits of the plan itself */}
      {selectedProduct?.includedTokens && (
        <Grid
          size={{ xs: 12 }}
          container
          spacing={1}
          wrap="nowrap"
          sx={{ alignItems: "flex-start" }}
        >
          {hasMultipleLimits && (
            <Checkbox
              checked={tokensPerYear > 0}
              disabled
              controlSx={{ mt: CHECKBOX_TOP_OFFSET }}
            />
          )}
          <TextField
            type="number"
            label={`${capitalize(CREDITS_DISPLAY_NAME)} per year`}
            error={
              tokensPerYear < 0
                ? `Please enter a valid number of ${CREDITS_DISPLAY_NAME}`
                : undefined
            }
            text={String(tokensPerYear)}
            onTextChanged={(newTokens: string) => {
              dispatch(setTokensPerYear(Number(newTokens)));
            }}
            controlStyle={{
              mt: 2,
              "& .MuiOutlinedInput-input": {
                p: `${FIELD_VERTICAL_PADDING} ${FIELD_HORIZONTAL_PADDING}`,
              },
            }}
            fullWidth
            disabled={selectedProduct.includedTokens.isFixed}
          />
        </Grid>
      )}
    </Grid>
  );
}
